import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, X } from 'lucide-react';
import { aiApi, type ChatContentBlock, type ChatMessage as ApiChatMessage, type ChatToolUseBlock, type ChatTextBlock, type ChatToolResultBlock } from '../../api/endpoints/ai';
import { ordersApi } from '../../api/endpoints/orders';
import { useProductForm } from '../../contexts/ProductFormContext';
import { useCouponForm } from '../../contexts/CouponFormContext';
import { usePaylinkForm } from '../../contexts/PaylinkFormContext';
import { useImagePicker } from '../../contexts/ImagePickerContext';
import { TOOLS, dispatchTool, type ToolContext } from './zeroChat/tools';
import { renderMarkdown } from './zeroChat/markdown';
import { ImagePickerOverlay } from './zeroChat/ImagePickerOverlay';

interface UiMessage {
  from: 'bot' | 'user';
  text: string;
  streaming?: boolean;
}

const SUGGESTIONS = [
  'كم مبيعات اليوم؟',
  'مين أفضل عميل؟',
  'وش المنتج الأكثر مبيعاً؟',
  'كيف أضيف منتج جديد؟',
];

const NO_SALES_PROMPT = 'كيف اجيب اول طلب؟';
const GROW_SALES_PROMPT = 'كيف أرفع المبيعات؟';

// Cap render rate so a bursty SSE delta (e.g. 30 tokens at once) reveals
// smoothly instead of dumping in one frame. Keep it tight so the user sees
// words landing as fast as they arrive when pacing is natural.
const STREAM_DRAIN_INTERVAL_MS = 12;

// Match a token that is purely markdown structural syntax with no content yet.
// We hold these back until the following content word arrives so the user
// never sees a bare "###" before its heading text materializes.
const MARKDOWN_PREFIX_RE = /^(#{1,6}|[-*+]|>|\d+[.)]|`{1,3}|\*{1,2}|~~|_{1,2}|\|)$/;

/**
 * Incremental streaming buffer. Accept arbitrary text chunks from SSE and
 * emit complete "render units" (a word plus trailing whitespace) so partial
 * markdown syntax never reaches the DOM.
 */
class StreamBuffer {
  private pending = '';
  // When non-empty, we're holding a markdown-prefix word ("###", "**", "-",
  // etc.) waiting for the next content word so they emit together.
  private held = '';

  /** Push a delta. Returns 0+ render units that are safe to display now. */
  push(chunk: string): string[] {
    this.pending += chunk;
    const flushed: string[] = [];

    while (true) {
      const m = this.pending.match(/\s+/);
      if (!m || m.index === undefined) break;

      const word = this.pending.slice(0, m.index);
      const ws = m[0];
      const consumeLen = m.index + ws.length;

      if (!word) {
        // Leading whitespace. Stick it onto the held prefix if we have one,
        // otherwise let it through as its own unit.
        if (this.held) this.held += ws;
        else flushed.push(ws);
        this.pending = this.pending.slice(consumeLen);
        continue;
      }

      const isPrefix = MARKDOWN_PREFIX_RE.test(word);
      if (this.held) {
        flushed.push(this.held + word + ws);
        this.held = '';
      } else if (isPrefix) {
        this.held = word + ws;
      } else {
        flushed.push(word + ws);
      }
      this.pending = this.pending.slice(consumeLen);
    }

    return flushed;
  }

  /** Drain everything (call once after the stream ends). */
  flush(): string[] {
    const remaining = this.held + this.pending;
    this.held = '';
    this.pending = '';
    return remaining ? [remaining] : [];
  }
}

const MAX_TOOL_LOOP_ITERATIONS = 6;

// Tiny static face used for message-row avatars. Mirrors PersonaFace's
// open-eye geometry but skips the blink interval (one per message would be
// wasteful and visually chaotic).
function BotFaceSmall({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size, display: 'block' }}>
      <path d="M 28 40 L 38 40 L 41 44 L 41 53 L 38 57 L 28 57 L 25 53 L 25 44 Z" fill="#D2E9FE" />
      <path d="M 62 40 L 72 40 L 75 44 L 75 53 L 72 57 L 62 57 L 59 53 L 59 44 Z" fill="#D2E9FE" />
      <path d="M 44 72 L 56 72 L 54 76 L 46 76 Z" fill="#D2E9FE" />
    </svg>
  );
}

function PersonaFace({ size = 56 }: { size?: number }) {
  const [eyesClosed, setEyesClosed] = useState(false);
  useEffect(() => {
    const t = setInterval(() => {
      setEyesClosed(true);
      setTimeout(() => setEyesClosed(false), 130);
    }, 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size, display: 'block', overflow: 'visible' }}>
      {eyesClosed ? (
        <g stroke="#D2E9FE" strokeWidth="3" strokeLinecap="round" fill="none">
          <path d="M 26 50 Q 33 45 40 50" />
          <path d="M 60 50 Q 67 45 74 50" />
        </g>
      ) : (
        <g>
          <path d="M 28 40 L 38 40 L 41 44 L 41 53 L 38 57 L 28 57 L 25 53 L 25 44 Z" fill="#D2E9FE" />
          <path d="M 62 40 L 72 40 L 75 44 L 75 53 L 72 57 L 62 57 L 59 53 L 59 44 Z" fill="#D2E9FE" />
          <rect x="30" y="43" width="4" height="4" rx="0.5" fill="#fff" />
          <rect x="64" y="43" width="4" height="4" rx="0.5" fill="#fff" />
          <rect x="36" y="51" width="1.5" height="1.5" fill="#fff" opacity="0.7" />
          <rect x="70" y="51" width="1.5" height="1.5" fill="#fff" opacity="0.7" />
        </g>
      )}
      <path d="M 44 72 L 56 72 L 54 76 L 46 76 Z" fill="#D2E9FE" />
    </svg>
  );
}

export function ZeroChat() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productForm = useProductForm();
  const couponForm = useCouponForm();
  const paylinkForm = usePaylinkForm();
  const imagePicker = useImagePicker();

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<UiMessage[]>([
    {
      from: 'bot',
      text: 'هلا فيك 👋 أنا «بيمو» مساعدك الذكي. اسألني عن مبيعاتك، عملاءك، أو كيف أسوي شي.',
    },
  ]);
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const apiHistoryRef = useRef<ApiChatMessage[]>([]);
  const inFlightRef = useRef(false);
  // One id per session lifecycle so the admin dashboard can group all turns
  // of a single chat together. Generated lazily on first send.
  const conversationIdRef = useRef<string | null>(null);
  const turnNumberRef = useRef(0);

  // Detect "no paid orders yet" so we can surface a "how do I grow sales"
  // suggestion. /orders/counts already filters to status=payed and returns
  // counts.all as the paid total.
  const { data: orderCounts } = useQuery({
    queryKey: ['orders', 'counts', storeId],
    queryFn: () => ordersApi.statusCounts(storeId!),
    enabled: !!storeId && open,
    staleTime: 5 * 60 * 1000,
  });
  const hasNoPaidOrders = orderCounts != null && (orderCounts.all ?? 0) === 0;
  const suggestions = hasNoPaidOrders
    ? [NO_SALES_PROMPT, ...SUGGESTIONS]
    : [GROW_SALES_PROMPT, ...SUGGESTIONS];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, typing]);

  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  const send = async (text?: string) => {
    if (inFlightRef.current) return;
    const userText = (text ?? input).trim();
    if (!userText || !storeId) return;

    inFlightRef.current = true;
    setMessages((m) => [...m, { from: 'user', text: userText }]);
    setInput('');
    setTyping(true);

    apiHistoryRef.current = [...apiHistoryRef.current, { role: 'user', content: userText }];

    const ctx: ToolContext = {
      storeId,
      navigate,
      productForm: {
        openCreate: productForm.openCreate,
        openEdit: productForm.openEdit,
        formRef: productForm.formRef,
      },
      couponForm: {
        openCreate: couponForm.openCreate,
        formRef: couponForm.formRef,
      },
      paylinkForm: {
        openCreate: paylinkForm.openCreate,
        formRef: paylinkForm.formRef,
      },
      imagePicker: {
        requestImageChoice: imagePicker.requestImageChoice,
      },
      queryClient,
    };

    try {
      // Tracks whether the user actually saw any bot text this exchange. If the
      // model only ever calls tools (loop hits the cap) or returns an empty
      // turn, we surface a fallback instead of leaving the chat silent.
      let producedReply = false;
      for (let i = 0; i < MAX_TOOL_LOOP_ITERATIONS; i++) {
        if (!conversationIdRef.current) {
          conversationIdRef.current = (typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
        }
        turnNumberRef.current += 1;

        // Per-iteration streaming state. The buffer feeds a queue that drains
        // at STREAM_DRAIN_INTERVAL_MS so even a bursty SSE chunk reveals
        // smoothly. We only mount the bot bubble once the first unit arrives,
        // so the typing dots remain visible until the model actually starts
        // talking.
        let messageIdx: number | null = null;
        let liveText = '';
        const buffer = new StreamBuffer();
        const queue: string[] = [];
        let drainTimer: number | null = null;

        const ensureMessage = () => {
          if (messageIdx != null) return;
          setTyping(false);
          setMessages((m) => {
            messageIdx = m.length;
            return [...m, { from: 'bot' as const, text: '', streaming: true }];
          });
        };

        const startDrain = () => {
          if (drainTimer != null) return;
          drainTimer = window.setInterval(() => {
            if (!queue.length) {
              if (drainTimer != null) {
                window.clearInterval(drainTimer);
                drainTimer = null;
              }
              return;
            }
            ensureMessage();
            const unit = queue.shift()!;
            liveText += unit;
            const snapshot = liveText;
            const idx = messageIdx;
            setMessages((m) => {
              if (idx == null || !m[idx]) return m;
              const copy = m.slice();
              copy[idx] = { ...copy[idx], text: snapshot };
              return copy;
            });
          }, STREAM_DRAIN_INTERVAL_MS);
        };

        const waitForDrain = () =>
          new Promise<void>((resolve) => {
            const tick = () => {
              if (queue.length === 0 && drainTimer == null) resolve();
              else setTimeout(tick, STREAM_DRAIN_INTERVAL_MS);
            };
            tick();
          });

        const response = await aiApi.chatStream(
          storeId,
          {
            messages: apiHistoryRef.current,
            tools: TOOLS,
            conversationId: conversationIdRef.current,
            turnNumber: turnNumberRef.current,
          },
          {
            onText: (delta) => {
              const units = buffer.push(delta);
              if (units.length) {
                queue.push(...units);
                startDrain();
              }
            },
            onError: () => {
              // Surfaced via the throw inside chatStream; just stop drain.
            },
          },
        );

        // Drain whatever stayed in the buffer when the stream ended.
        const tail = buffer.flush();
        if (tail.length) {
          queue.push(...tail);
          startDrain();
        }
        await waitForDrain();

        // Mark the message done (no more streaming) and snap to the canonical
        // assembled text so the UI is byte-identical to the persisted history.
        const assembledText = response.content
          .filter((b): b is ChatTextBlock => b.type === 'text')
          .map((b) => b.text)
          .join('\n');
        if (messageIdx != null) {
          const idx = messageIdx;
          setMessages((m) => {
            if (!m[idx]) return m;
            const copy = m.slice();
            copy[idx] = { ...copy[idx], text: assembledText.trim() || liveText, streaming: false };
            return copy;
          });
        }
        if (assembledText.trim() || liveText.trim()) producedReply = true;

        apiHistoryRef.current = [
          ...apiHistoryRef.current,
          { role: 'assistant', content: response.content },
        ];

        const toolUses = response.content.filter(
          (b): b is ChatToolUseBlock => b.type === 'tool_use',
        );

        if (toolUses.length === 0) break;

        // Tools are about to run; show typing dots again so the user knows
        // there's more on the way.
        setTyping(true);

        // The `navigate_to` schema tells the model its `reason` is shown to
        // the user. The dispatcher only navigates — nothing renders the
        // reason — so a turn that's "tool_use only" feels like a silent
        // page jump. Promote the reason into the bot's text bubble when
        // the model didn't speak this turn.
        if (!assembledText.trim() && !liveText.trim()) {
          const nav = toolUses.find((t) => {
            if (t.name !== 'navigate_to') return false;
            const input = t.input as Record<string, unknown> | undefined;
            return typeof input?.reason === 'string' && (input.reason as string).trim().length > 0;
          });
          if (nav) {
            const reason = ((nav.input as Record<string, unknown>).reason as string).trim();
            producedReply = true;
            if (messageIdx != null) {
              const idx = messageIdx;
              setMessages((m) => {
                if (!m[idx]) return m;
                const copy = m.slice();
                copy[idx] = { ...copy[idx], text: reason };
                return copy;
              });
            } else {
              setMessages((m) => [...m, { from: 'bot', text: reason }]);
            }
          }
        }

        const toolResults: ChatToolResultBlock[] = [];
        for (const t of toolUses) {
          const { result, isError } = await dispatchTool(t.name, t.input, ctx);
          toolResults.push({
            type: 'tool_result',
            tool_use_id: t.id,
            content: result,
            ...(isError ? { is_error: true } : {}),
          });
        }
        apiHistoryRef.current = [
          ...apiHistoryRef.current,
          { role: 'user', content: toolResults },
        ];
      }
      if (!producedReply) {
        // Loop ended (hit the iteration cap or got an empty turn) without the
        // bot ever speaking. Never leave the user staring at nothing.
        setMessages((m) => [
          ...m,
          { from: 'bot' as const, text: 'جمعت المعلومات بس ما قدرت أكمّل الرد. جرّب تعيد صياغة سؤالك أو اسأله بشكل أبسط.' },
        ]);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'حدث خطأ';
      setMessages((m) => [...m, { from: 'bot', text: `ما قدرت أجاوبك الحين (${errMsg}). جرب مرة ثانية.` }]);
    } finally {
      setTyping(false);
      inFlightRef.current = false;
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="مساعدك الذكي بيمو"
        style={{
          position: 'fixed',
          bottom: 22,
          left: 22,
          zIndex: 1000,
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'linear-gradient(155deg, #1E2B42, #0A2248 60%, #010101)',
          border: '1.5px solid rgba(210,233,254,0.3)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.5), 0 0 0 4px rgba(210,233,254,0.06)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 220ms',
          padding: 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.06)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      >
        <PersonaFace size={48} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: '#E89292',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              fontFamily: "'Inter', sans-serif",
              width: 18,
              height: 18,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #0A2248',
            }}
          >
            {unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 22,
        left: 22,
        zIndex: 1000,
        width: 380,
        height: 560,
        maxHeight: 'calc(100vh - 44px)',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 18,
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        direction: 'rtl',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(155deg, #1E2B42, #0A2248 60%, #010101)',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid rgba(210,233,254,0.1)',
        }}
      >
        <PersonaFace size={42} />
        <div style={{ flex: 1, color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>بيمو</span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 9.5,
                color: '#6be0a3',
                fontWeight: 700,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: '#1E8F5E',
                  boxShadow: '0 0 0 3px rgba(30,143,94,0.22)',
                }}
              />
              متاح الآن
            </span>
          </div>
          <div style={{ fontSize: 10.5, color: '#bfbdbd', marginTop: 2 }}>مساعدك الذكي · يفهم متجرك</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: 'none',
            width: 30,
            height: 30,
            borderRadius: 8,
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={14} />
        </button>
      </div>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: 'var(--color-bg-tertiary)',
        }}
      >
        {messages.map((m, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              flexDirection: m.from === 'user' ? 'row-reverse' : 'row',
            }}
          >
            {m.from === 'bot' && (
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: 'linear-gradient(155deg,#1E2B42,#0A2248)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <BotFaceSmall size={18} />
              </div>
            )}
            <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  background: m.from === 'user' ? '#D2E9FE' : 'var(--color-bg-secondary)',
                  color: m.from === 'user' ? '#0A2248' : 'var(--color-text-primary)',
                  border: m.from === 'bot' ? '1px solid var(--color-border-default)' : 'none',
                  padding: '9px 13px',
                  borderRadius: 12,
                  borderEndEndRadius: m.from === 'user' ? 4 : 12,
                  borderEndStartRadius: m.from === 'bot' ? 4 : 12,
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  fontWeight: m.from === 'user' ? 600 : 400,
                }}
              >
                {m.from === 'bot' ? renderMarkdown(m.text) : m.text}
              </div>
            </div>
          </div>
        ))}
        {typing && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: 'linear-gradient(155deg,#1E2B42,#0A2248)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BotFaceSmall size={18} />
            </div>
            <div
              style={{
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-default)',
                padding: '11px 14px',
                borderRadius: 12,
                borderEndStartRadius: 4,
                display: 'flex',
                gap: 4,
              }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    background: '#D2E9FE',
                    animation: `zeroChatDot 1.2s ${i * 0.18}s infinite ease-in-out`,
                  }}
                />
              ))}
            </div>
          </div>
        )}
        <style>{`@keyframes zeroChatDot { 0%,80%,100% { opacity: 0.3; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-4px); } }`}</style>
      </div>

      {!messages.some((m) => m.from === 'user') && (
        <a
          href="https://wa.me/+966558500180"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            margin: '10px 14px 0',
            padding: '10px 12px',
            borderRadius: 12,
            background: 'rgba(37, 211, 102, 0.10)',
            border: '1px solid rgba(37, 211, 102, 0.35)',
            color: 'var(--color-text-primary)',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12.5,
            fontFamily: 'inherit',
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              background: '#25D366',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
              <path d="M19.05 4.91A10 10 0 0 0 4.16 17.34L3 21l3.74-1.13a10 10 0 0 0 4.78 1.22h.01A10 10 0 0 0 19.05 4.91Zm-7.52 14.59h-.01a8.32 8.32 0 0 1-4.24-1.16l-.3-.18-2.21.67.7-2.15-.2-.31a8.34 8.34 0 1 1 14.5-5.6 8.34 8.34 0 0 1-8.24 8.73Zm4.59-6.23c-.25-.13-1.48-.73-1.71-.81-.23-.08-.4-.13-.56.13-.17.25-.65.81-.79.97-.15.17-.29.19-.54.06-.25-.13-1.06-.39-2.01-1.24a7.49 7.49 0 0 1-1.39-1.73c-.15-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.13-.56-1.35-.77-1.85-.2-.49-.4-.42-.56-.43h-.48a.92.92 0 0 0-.67.31c-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.02 2.57.13.17 1.76 2.69 4.27 3.77.6.26 1.06.42 1.42.54.6.19 1.14.16 1.57.1.48-.07 1.48-.6 1.69-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.17-.48-.29Z" />
            </svg>
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.2 }}>
            <span style={{ fontWeight: 700 }}>تواصل مع الدعم البشري عبر واتساب</span>
            <span style={{ fontSize: 10.5, color: 'var(--color-text-secondary)' }}>
              يردّ عليك فريق زيرو مباشرة
            </span>
          </span>
        </a>
      )}

      {messages.length <= 1 && (
        <div
          style={{
            padding: '8px 14px 0',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            background: 'var(--color-bg-tertiary)',
          }}
        >
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => send(s)}
              style={{
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-default)',
                borderRadius: 999,
                padding: '5px 11px',
                fontSize: 11,
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          padding: 12,
          borderTop: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-secondary)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="اسأل بيمو أي شي..."
          style={{
            flex: 1,
            background: 'var(--color-bg-tertiary)',
            border: '1px solid var(--color-border-default)',
            borderRadius: 10,
            padding: '10px 13px',
            fontSize: 12.5,
            color: 'var(--color-text-primary)',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={() => send()}
          disabled={!input.trim()}
          style={{
            background: input.trim() ? '#D2E9FE' : 'var(--color-bg-tertiary)',
            color: input.trim() ? '#0A2248' : 'var(--color-text-muted)',
            border: 'none',
            borderRadius: 10,
            width: 38,
            height: 38,
            cursor: input.trim() ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 140ms',
          }}
        >
          <Send size={14} />
        </button>
      </div>

      {/* Image picker takes over the panel when the bot calls request_image_choice */}
      <ImagePickerOverlay />
    </div>
  );
}
